import React, { useState, useCallback, useRef } from 'react';
import { FiUpload, FiX, FiImage, FiLoader, FiCamera } from 'react-icons/fi';
import api from '@/services/api';

const ImageUpload = ({ images, setImages, maxImages = 5 }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleFileChange = useCallback(async (e) => {
    const files = Array.from(e.target.files);
    
    if (images.length + files.length > maxImages) {
      setError(`Máximo ${maxImages} imagens permitidas`);
      return;
    }

    // Validate each file
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        setError('Apenas imagens são permitidas');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('Cada imagem deve ter no máximo 5MB');
        return;
      }
    }

    setError('');
    setUploading(true);
    setProgress(0);

    const newImages = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post('/upload/image', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              ((i + progressEvent.loaded / progressEvent.total) / files.length) * 100
            );
            setProgress(percentCompleted);
          },
        });

        newImages.push({
          url: response.data.url,
          public_id: response.data.public_id
        });
      } catch (err) {
        console.error('Upload error:', err);
        setError(err.response?.data?.detail || 'Erro ao fazer upload');
      }
    }

    if (newImages.length > 0) {
      setImages([...images, ...newImages]);
    }
    
    setUploading(false);
    setProgress(0);
  }, [images, maxImages, setImages]);

  const removeImage = async (index) => {
    const imageToRemove = images[index];
    
    // Try to delete from cloud if it has a public_id
    if (imageToRemove.public_id && imageToRemove.public_id !== 'local') {
      try {
        await api.delete(`/upload/image/${encodeURIComponent(imageToRemove.public_id)}`);
      } catch (err) {
        console.error('Error deleting image:', err);
      }
    }
    
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
  };

  return (
    <div className="space-y-4">
      {/* Image Preview Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
          {images.map((image, index) => (
            <div key={index} className="relative group aspect-square">
              <img
                src={image.url || image}
                alt={`Produto ${index + 1}`}
                className="w-full h-full object-cover rounded-xl border border-gray-200"
              />
              <button
                type="button"
                onClick={() => removeImage(index)}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
              >
                <FiX className="text-sm" />
              </button>
              {index === 0 && (
                <span className="absolute bottom-1 left-1 bg-[#DC143C] text-white text-xs px-2 py-0.5 rounded">
                  Principal
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload Buttons */}
      {images.length < maxImages && !uploading && (
        <div className="space-y-3">
          {/* Camera and Gallery Buttons */}
          <div className="grid grid-cols-2 gap-3">
            {/* Camera Button */}
            <button
              type="button"
              onClick={() => cameraInputRef.current?.click()}
              className="flex flex-col items-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-[#DC143C] hover:bg-red-50 transition-all"
            >
              <div className="w-12 h-12 bg-[#DC143C] rounded-full flex items-center justify-center">
                <FiCamera className="text-2xl text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700">Tirar Foto</span>
            </button>
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileChange}
              className="hidden"
            />

            {/* Gallery Button */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex flex-col items-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-[#DC143C] hover:bg-red-50 transition-all"
            >
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                <FiImage className="text-2xl text-gray-500" />
              </div>
              <span className="text-sm font-medium text-gray-700">Galeria</span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              className="hidden"
            />
          </div>
          
          <p className="text-xs text-gray-500 text-center">
            JPEG, PNG ou WebP • Máx. 5MB cada
          </p>
        </div>
      )}

      {/* Uploading State */}
      {uploading && (
        <div className="border-2 border-[#DC143C] bg-red-50 rounded-xl p-6">
          <div className="flex flex-col items-center gap-2">
            <FiLoader className="text-3xl text-[#DC143C] animate-spin" />
            <p className="text-sm text-gray-600">Enviando... {progress}%</p>
            <div className="w-full max-w-xs bg-gray-200 rounded-full h-2">
              <div 
                className="bg-[#DC143C] h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Counter */}
      <p className="text-sm text-gray-500 text-center">
        {images.length}/{maxImages} imagens
      </p>

      {/* Error Message */}
      {error && (
        <p className="text-sm text-red-500 text-center bg-red-50 p-2 rounded-lg">
          {error}
        </p>
      )}
    </div>
  );
};

export default ImageUpload;
